// src/components/QrPrivadoButton.jsx
import React, { useState } from 'react';
import {
  Button,
  Modal,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Lock as LockIcon, Print as PrintIcon } from '@mui/icons-material';
import api from '../api/api';

// Función auxiliar para acortar el token
const abreviarToken = (token) => {
  if (!token || token.length < 12) {
    return token;
  }
  // Tomamos los primeros 6 y los últimos 6 caracteres
  return `${token.substring(0, 6)}...${token.substring(token.length - 6)}`;
};

const QrPrivadoButton = ({ id_paciente, pacienteNombre = 'Paciente' }) => {
  const [loadingQR, setLoadingQR] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrUrl, setQrUrl] = useState('');
  const [tokenPrivado, setTokenPrivado] = useState('');
  const [generandoPDF, setGenerandoPDF] = useState(false);

  const PROMO_IMAGE_URL = "https://vetid-galaxia-dog-place.vercel.app/promo-diciembre.jpg";

  const generarQRPrivado = async () => {
  if (!id_paciente) return;
  setLoadingQR(true);
  try {
    const resPaciente = await api.get(`/pacientes/${id_paciente}`);
    const p = resPaciente.data;

    // VALIDACIÓN 1: ¿TIENE QR?
    if (!p.qr_code || p.qr_code === 'null' || p.qr_code.trim() === '') {
      alert("Este paciente no tiene código QR asignado.\n\nPrimero genera el QR público en la ficha del paciente.");
      setLoadingQR(false);
      return;
    }

    const resCliente = await api.get(`/clientes/${p.id_cliente}`);
    const token = resCliente.data?.token_cliente;

    // VALIDACIÓN 2: ¿TIENE TOKEN?
    if (!token) {
      alert("Este cliente no tiene token privado.\n\nVe a Editar Cliente → Generar Token Privado.");
      setLoadingQR(false);
      return;
    }

    // TODO BIEN → GENERAR QR
    const qrBackendUrl = `${api.defaults.baseURL}/pacientes/qr/privado/${p.qr_code}`;  // debe ir completa li quitas localhost dejara de funcionar es por el token
    setQrUrl(qrBackendUrl);
    setTokenPrivado(token);
    setQrModalOpen(true);

  } catch (err) {
    alert("Error: " + (err.response?.data?.detail || err.message));
  } finally {
    setLoadingQR(false);
  }
};

  const imprimirTarjetita = async () => {
    try {
      setGenerandoPDF(true);
      const resPaciente = await api.get(`/pacientes/${id_paciente}`);
      const p = resPaciente.data;
      const resCliente = await api.get(`/clientes/${p.id_cliente}`);
      const c = resCliente.data;
      const token = c.token_cliente || 'Sin token';
      
      // APLICAMOS LA FUNCIÓN DE ABREVIACIÓN AQUÍ PARA LA IMPRESIÓN
      const tokenAbreviado = abreviarToken(token);

      const qrResponse = await api.get(`/pacientes/qr/privado/${p.qr_code}`, {
        responseType: 'blob'
      });
      const qrBlob = qrResponse.data;
      const qrDataUrl = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(qrBlob);
      });

      const printTab = window.open('', '_blank');
      printTab.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>4 Tarjetitas QR - ${p.nombre_paciente}</title>
          <meta charset="utf-8">
          <style>
            @page { size: letter; margin: 1cm; }
            body { margin: 0; padding: 0; font-family: Arial, sans-serif; }
            .page { display: grid; grid-template-columns: 1fr 1fr; grid-template-rows: 1fr 1fr; height: 100vh; gap: 10px; padding: 10px; box-sizing: border-box; }
            .card { border: 2px solid #1a3d7c; border-radius: 12px; padding: 12px; text-align: center; background: white; box-shadow: 0 4px 10px rgba(0,0,0,0.1); font-size: 10px; display: flex; flex-direction: column; justify-content: space-between; }
            .qr { width: 160px; height: 160px; margin: 10px auto; padding: 6px; background: white; border: 3px solid #2e7d32; border-radius: 8px; }
            .token { 
              font-family: 'Courier New', monospace; 
              font-size: 8px; 
              word-break: break-all; 
              background: #f0f0f0; 
              padding: 6px; 
              border-radius: 5px; 
              margin: 8px 0; 
              /* Cambiamos el color para que el texto abreviado se vea mejor */
              color: #d32f2f; 
            }
            hr { border: 1px dashed #2e7d32; margin: 8px 0; }
            .header { color: #1a3d7c; font-weight: bold; font-size: 12px; }
            .footer { font-size: 7px; color: #666; }
          </style>
        </head>
        <body onload="window.print()">
          <div class="page">
            ${[1,2,3,4].map(() => `
              <div class="card">
                <div class="header">GALAXIA VET</div>
                <small style="color:#2e7d32;">Ficha Digital Segura</small>
                <hr>
                <strong>${p.nombre_paciente}</strong><br>
                <small>Dueño: ${c.nombre_cliente || 'N/A'}</small><br>
                <img src="${qrDataUrl}" class="qr">
                <div class="token">Token: ${tokenAbreviado}</div> <!-- AQUI USAMOS EL TOKEN ABREVIADO -->
                <small>Receta • Vacuna • Terapias</small>
                <hr>
                
                <div class="footer">galaxia-alpha.vercel.app</div>
              </div>
            `).join('')}
          </div>
        </body>
        </html>
      `);
      printTab.document.close();
      printTab.focus();
    } catch (err) {
      alert("Error: " + (err.response?.data?.detail || err.message));
    } finally {
      setGenerandoPDF(false);
    }
  };

  return (
    <>
      {/* BOTÓN PRINCIPAL */}
      <Tooltip title="QR Privado con Token">
        <Button
          variant="contained"
          color="secondary"
          size="large"
          startIcon={<LockIcon />}
          onClick={generarQRPrivado}
          disabled={loadingQR}
          sx={{
            bgcolor: '#d32f2f',
            '&:hover': { bgcolor: '#b71c1c' },
            fontWeight: 'bold',
            px: 4,
            py: 1.5,
            borderRadius: 3,
            boxShadow: '0 4px 15px rgba(211, 47, 47, 0.4)'
          }}
        >
          {loadingQR ? (
            <>
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              Generando...
            </>
          ) : (
            '-QR-'
          )}
        </Button>
      </Tooltip>

      {/* MODAL QR */}
      <Modal open={qrModalOpen} onClose={() => setQrModalOpen(false)}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', sm: 400 },
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: 4,
          textAlign: 'center'
        }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
            QR Privado Generado
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            <strong>{pacienteNombre}</strong>
          </Typography>
          {qrUrl && (
            <>
              <img
                src={qrUrl}
                alt="QR Privado"
                style={{ width: '100%', maxWidth: 300, margin: '20px 0', border: '1px solid #ccc' }}
                onLoad={() => console.log("QR cargado")}
                onError={(e) => e.target.src = 'https://via.placeholder.com/300?text=QR+ERROR'}
              />
              <Typography variant="body2" sx={{ wordBreak: 'break-all', background: '#f5f5f5', p: 2, borderRadius: 2, fontSize: '0.8rem' }}>
                URL QR: {qrUrl}
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1, color: '#666' }}>
                Token: <strong>{abreviarToken(tokenPrivado)}</strong> {/* AQUI SE MUESTRA ABREVIADO EN EL MODAL */}
              </Typography>
            </>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button
              variant="contained"
              color="success"
              size="large"
              startIcon={<PrintIcon />}
              onClick={imprimirTarjetita}
              disabled={generandoPDF}
              sx={{
                bgcolor: '#2e7d32',
                '&:hover': { bgcolor: '#1b5e20' },
                fontWeight: 'bold',
                px: 6,
                py: 2,
                borderRadius: 4,
                boxShadow: '0 6px 20px rgba(46, 125, 50, 0.4)'
              }}
            >
              IMPRIMIR 4 TARJETITAS (1/4 HOJA)
            </Button>
          </Box>

          <Button variant="contained" color="primary" onClick={() => setQrModalOpen(false)} sx={{ mt: 3 }}>
            Cerrar
          </Button>
        </Box>
      </Modal>
    </>
  );
};

export default QrPrivadoButton;